// Portfolio pure functions. 1:1 portiert aus V1 useEvePortfolio.js.
// - createDefaultPortfolio
// - applyTradeResult (zurück gibt next state)
// - getRecommendedCapital
// - formatPortfolioForPrompt
// - shouldMigrate ($10k V1-altes Portfolio → $500k upgraden)

import type { PortfolioState } from '@/types/portfolio';

export const DEFAULT_CAPITAL = 500_000;
export const RISK_PER_TRADE = 0.02; // 2% Risiko pro Trade

/** V1: portfolios mit startCapital < $100k werden auf $500k upgraded. */
export const MIGRATION_THRESHOLD = 100_000;

export function createDefaultPortfolio(now: Date = new Date()): PortfolioState {
  return {
    startCapital: DEFAULT_CAPITAL,
    currentCapital: DEFAULT_CAPITAL,
    startedAt: now.toISOString(),
    resetAt: null,
    totalTrades: 0,
    totalPnlAbsolute: 0,
  };
}

/** V1: Reset legt ein neues Portfolio an mit resetAt = jetzt. */
export function resetPortfolioState(now: Date = new Date()): PortfolioState {
  return {
    ...createDefaultPortfolio(now),
    resetAt: now.toISOString(),
  };
}

/** V1: V1-altes $10k-Portfolio → $500k upgraden. */
export function shouldMigrate(p: PortfolioState): boolean {
  return p.startCapital < MIGRATION_THRESHOLD;
}

export function migratePortfolio(p: PortfolioState, now: Date = new Date()): PortfolioState {
  return {
    ...p,
    startCapital: DEFAULT_CAPITAL,
    currentCapital: DEFAULT_CAPITAL,
    resetAt: now.toISOString(),
  };
}

/** V1: 2% des aktuellen Kapitals, gerundet auf 2 Nachkommastellen. */
export function getRecommendedCapital(p: PortfolioState): number {
  return parseFloat((p.currentCapital * RISK_PER_TRADE).toFixed(2));
}

/**
 * Pure: gibt neuen Portfolio-State nach einem Trade-Outcome zurück. V1-Rundung
 * auf 2 Nachkommastellen für currentCapital + totalPnlAbsolute.
 */
export function applyTradeResult(p: PortfolioState, pnlAbsolute: number): PortfolioState {
  return {
    ...p,
    currentCapital: parseFloat((p.currentCapital + pnlAbsolute).toFixed(2)),
    totalTrades: p.totalTrades + 1,
    totalPnlAbsolute: parseFloat((p.totalPnlAbsolute + pnlAbsolute).toFixed(2)),
  };
}

/**
 * V1 formatPortfolioForPrompt — lesbarer Block für EVE-System-Prompts.
 * Verhalten 1:1: totalPct mit toFixed(2), Vorzeichen je nach P&L.
 */
export function formatPortfolioForPrompt(p: PortfolioState): string {
  const totalPctNum = ((p.currentCapital - p.startCapital) / p.startCapital) * 100;
  const totalPct = totalPctNum.toFixed(2);
  const sign = totalPctNum > 0 ? '+' : '';
  const pnlSign = p.totalPnlAbsolute >= 0 ? '+' : '';
  return [
    `EVE VIRTUELLES PORTFOLIO:`,
    `  Startkapital: $${p.startCapital.toLocaleString()}`,
    `  Aktuell:      $${p.currentCapital.toLocaleString()} (${sign}${totalPct}%)`,
    `  Trades:       ${p.totalTrades}`,
    `  Gesamt P&L:   ${pnlSign}$${p.totalPnlAbsolute.toLocaleString()}`,
    `  Seit:         ${p.startedAt.slice(0, 10)}`,
  ].join('\n');
}
